import { useParams } from "react-router-dom";
import { Building2, User, MapPin, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { TrustBadge, TrustRing } from "@/components/TrustBadge";
import { FeedCard } from "@/pages/app/Feed";
import { TrustTimeline } from "@/components/TrustTimeline";
import { useApi } from "@/lib/useApi";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border border-white/[0.08] bg-[#111827] p-5" data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-white">{value ?? "—"}</p>
  </div>
);

export default function Profile() {
  const { id } = useParams();
  const { t } = useLang();
  const { data: p, setData: setP } = useApi(`/profiles/${id}`, { toastErrors: true });
  if (!p) return <p className="font-mono text-xs text-slate-500">…</p>;
  const Icon = p.kind === "company" ? Building2 : User;
  const follow = async () => {
    try { const { data } = await api.post(`/profiles/${id}/follow`); setP({ ...p, followed_by_me: data.following, followers: data.followers }); } catch (e) { toast.error(errMsg(e)); }
  };
  const like = async (iid) => {
    try { const { data } = await api.post(`/feed/${iid}/like`); setP({ ...p, intents: p.intents.map((x) => (x.id === iid ? data : x)) }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8" data-testid="public-profile">
      <section className="relative overflow-hidden rounded-md border border-white/[0.08] bg-[#111827] p-8 grid-lines">
        <div className="relative flex flex-wrap items-center gap-6">
          <span className="grid h-20 w-20 place-items-center rounded-full border border-emerald-400/30 bg-[#0B0F17]"><Icon className="h-8 w-8 text-cyan-300" strokeWidth={1.5} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{t(p.kind)} · {t("profile")}</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-white sm:text-4xl" data-testid="profile-name">{p.name}</h1>
            <p className="mt-1 text-sm text-slate-400">{p.headline}</p>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" />{p.location || "—"} · {p.sector || "—"} · {t("member_since")} {new Date(p.member_since).toLocaleDateString()}</p>
          </div>
          <div className="text-center">
            <p className="font-display text-2xl font-bold text-white" data-testid="profile-followers">{p.followers.toLocaleString()}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("followers")}</p>
            {!p.mine && (
              <button onClick={follow} data-testid="follow-btn"
                className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs transition-colors ${p.followed_by_me ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-slate-200 hover:border-emerald-400"}`}>
                {p.followed_by_me ? <UserCheck className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}{t(p.followed_by_me ? "following" : "follow")}
              </button>
            )}
          </div>
          <div className="text-center"><TrustRing value={p.trust_score} size={88} /><p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("trust")}</p></div>
        </div>
        <div className="relative mt-6 flex flex-wrap gap-2" data-testid="profile-badges">
          {p.badges.map((b) => <TrustBadge key={b} badge={b} />)}
          {!p.badges.length && <span className="text-xs text-slate-500">—</span>}
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t("stats_rel")} value={p.stats.relationships} testId="profile-stat-relationships" />
        <Stat label={t("stats_completed")} value={p.stats.completed} testId="profile-stat-completed" />
        <Stat label={t("stats_active")} value={p.stats.active} testId="profile-stat-active" />
        <Stat label={t("stats_outcomes")} value={p.stats.outcomes} testId="profile-stat-outcomes" />
        <Stat label={t("stats_rating")} value={p.stats.average_rating} testId="profile-stat-rating" />
        <Stat label={t("stats_interview_rating")} value={p.stats.interview_rating ? `${p.stats.interview_rating} ★ (${p.stats.interview_ratings})` : null} testId="profile-stat-interview-rating" />
      </div>

      <TrustTimeline pid={id} />

      <section className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="profile-outcomes">
        <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("completed_outcomes")}</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(p.stats.by_type).map(([k, v]) => (
            <span key={k} className={`rounded-md border px-3 py-2 text-sm ${["no_show", "misconduct"].includes(k) ? "border-rose-400/30 text-rose-300" : "border-emerald-400/30 text-emerald-200"}`}>{t(`oc_${k}`)} <b className="ml-1 font-mono">{v}</b></span>
          ))}
          {!Object.keys(p.stats.by_type).length && <span className="text-sm text-slate-500">{t("empty")}</span>}
        </div>
      </section>

      <section>
        <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("active_intents")}</p>
        <div className="grid gap-4 lg:grid-cols-2">
          {p.intents.map((it) => <FeedCard key={it.id} item={it} onLike={like} />)}
          {!p.intents.length && <p className="text-sm text-slate-500">{t("empty")}</p>}
        </div>
      </section>
    </div>
  );
}
